import { useState, useEffect } from 'react'
import axios from 'services/customAxios'
import { useAtom } from 'jotai'
import { pathOr, propOr, head } from 'ramda'
import { getDatesForCurrentWeek } from 'utils/date'

import { getItem } from 'utils/localstorage'
import {
  tableRecordsAtom,
  dataFetchLoadingAtom,
  globalPostAtom,
  pageAtom,
  rowsPerPageAtom,
  searchTermAtom,
  catAssocListAtom,
  sortByAtom,
  skipFilterAtom,
  refreshDataAtom,
  mapViewAtom,
} from 'constants/atoms'
import { getEndpoints } from 'constants/endpoints'

const responseExtractors = {
  default: (data) => data.data,
  workRequestsView: (data) => data.work_requests,
}

const FetchData = (selectedView, selectedSubView, selectedFilters) => {
  const [data, setData] = useState([])
  const [dailyCapacitiesData, setDailyCapacitiesData] = useState({})
  const [, setDataFetchLoading] = useAtom(dataFetchLoadingAtom)
  const [, setTableRecords] = useAtom(tableRecordsAtom)
  const [globalLoading, setGlobalLoading] = useAtom(globalPostAtom)
  const [page] = useAtom(pageAtom)
  const [rowsPerPage] = useAtom(rowsPerPageAtom)
  const [search] = useAtom(searchTermAtom)
  const [, setCATAssociationList] = useAtom(catAssocListAtom)
  const [sortBy] = useAtom(sortByAtom)
  const [, setSkipFilter] = useAtom(skipFilterAtom)
  const [refreshData, setRefreshData] = useAtom(refreshDataAtom)
  const [mapView] = useAtom(mapViewAtom)

  const { modifiedDates } = getDatesForCurrentWeek(new Date(), 'dd/MM', true)

  const fetchData = async () => {
    const {
      vendor: { vendor_id: vendorId },
    } = getItem('userData')
    const url = getEndpoints({ vendorId })[selectedView]

    try {
      if (selectedView !== 'dailyCapacitiesView') {
        await fetchOtherViewData(
          selectedView,
          selectedFilters,
          url,
          search,
          sortBy,
          page,
          rowsPerPage,
        )
      } else {
        await fetchDailyCapacityData()
      }
      setRefreshData(false)
    } catch (error) {
      console.error(`Failed to retrieve ${selectedView} details`, error)
      setDataFetchLoading(false)
      setGlobalLoading(false)
    }
  }

  const fetchOtherViewData = async (
    selectedView,
    selectedFilters,
    url,
    search,
    sortBy,
    page,
    rowsPerPage,
  ) => {
    let response

    if (['lotsView', 'tripsView', 'driversView'].includes(selectedView)) {
      setDataFetchLoading(true)
      response = await axios.post(url, {
        filter_by: selectedFilters[selectedView],
        page: page + 1,
        page_size: rowsPerPage,
        search,
        sort_by: sortBy,
      })
    } else {
      response = await axios.get(url)
    }

    if (response?.data?.status === 'success') {
      handleSuccessResponse(selectedView, response)
    }
  }

  const fetchDailyCapacityData = async () => {
    try {
      setDataFetchLoading(true)
      const { vendor_id: vendorId } = getItem('userData')
      const capacitiesEndPoint = getEndpoints({ vendorId }).capacities
      const capacitiesResponse = await axios.get(capacitiesEndPoint, {
        params: {
          from_date: modifiedDates[0],
          to_date: modifiedDates[modifiedDates.length - 1],
        },
      })
      const capacities = pathOr(
        [],
        ['data', 'additional_info', 'default_capacities'],
        capacitiesResponse,
      )
      const yardNumbers = Object.keys(capacities).filter(
        (key) => capacities[key].capacity_enabled_flag,
      )

      const extractDefaultCapacities = (capacities) => {
        let result = {}
        for (let key in capacities) {
          result[key] = capacities[key].capacity
        }
        return result
      }

      const data = {
        yards: yardNumbers,
        capacities: capacitiesResponse?.data?.vendor_capacities,
        transporterDefaultCapacities: extractDefaultCapacities(capacities),
      }

      setDailyCapacitiesData(data)
      setDataFetchLoading(false)
      setGlobalLoading(false)
    } catch (error) {
      console.error('Error fetching data:', error)
      setDataFetchLoading(false)
      setGlobalLoading(false)
    }
  }

  const handleSuccessResponse = (selectedView, response) => {
    const extractor =
      responseExtractors[selectedView] || responseExtractors.default
    const responseData = extractor(response.data)
    setData(responseData)
    setDataFetchLoading(false)
    let totalLength = ['tripsView', 'lotsView', 'driversView'].includes(
      selectedView,
    )
      ? response?.data.total_count
      : responseData?.length

    if (selectedView === 'workRequestsView') {
      totalLength = responseData?.filter(
        (row) => row.message_status === 'A',
      ).length
    }

    setTableRecords(totalLength)
    setGlobalLoading(false)
    setSkipFilter(false)
  }

  useEffect(() => {
    if (globalLoading) {
      setTimeout(() => {
        fetchData()
      }, 400)
    } else {
      fetchData()
    }
  }, [
    selectedView,
    selectedSubView,
    selectedFilters,
    globalLoading,
    rowsPerPage,
    page,
    search,
    sortBy,
    mapView,
  ])

  useEffect(() => {
    if (refreshData) {
      fetchData()
    }
  }, [refreshData])

  return { data, dailyCapacitiesData }
}

export const getBusinessDays = async (yard) => {
  try {
    const response = await axios.get(getEndpoints().workingDays, {
      headers: {
        'X-Yard': yard,
      },
    })
    return response
  } catch (error) {
    console.error(error)
  }
}

export const mapViewFetcher = async (
  selectedView,
  selectedSubView,
  selectedFilters,
  search,
) => {
  const { vendor_id: vendorId } = getItem('userData')
  const endpoints = getEndpoints({ vendorId })
  const url = endpoints['lotsView']
  let response

  try {
    response = await axios.post(url, {
      filter_by: selectedFilters[selectedView],
      page: 1,
      page_size: 5000,
      search,
    })

    if (response.data.status === 'success') {
      const totalLength = response?.data.total_count
      const dataForMapView = pathOr([], ['data', 'data'], response)
      return { data: dataForMapView, totalCount: totalLength }
    }
  } catch (error) {
    console.error(`Failed to retrieve ${selectedView} details`, error)
  }
}

export default FetchData
